import { useEffect, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { BootScreen, PageLoader, useSoundOnMount } from './ui';
import type { SiteId } from '@goodbuild/rules/sites';
import { tipsFor } from './tips';

/**
 * What the app shows while it is between pages (#100).
 *
 * **Every screen in here already waited well and the app around them did not.**
 * `Screen` draws the brand mark with a light running through it the moment a
 * query is out, so a table still loading looks like a table still loading. What
 * had no answer at all was the layer above: three places in `router.tsx`
 * returned an empty fragment, and an empty fragment is a rectangle of ground
 * with nothing on it.
 *
 * All three are ordinary and none of them is brief:
 *
 *   1. **The session check, on every single page load.** `useSession` asks the
 *      API whether the stored token is still good, so there is a network round
 *      trip between the bundle running and anything being drawn. The first
 *      thing anybody saw of this product, every time they opened it, was a
 *      blank page for the length of that request.
 *   2. **The redirect to sign in**, where the wait is a whole browser
 *      navigation and the fragment is on screen until it completes.
 *   3. **The handover to the storefront** for a path this origin does not
 *      serve — same shape, same blank.
 *
 * And one that is not blank today but will be: a navigation whose next route
 * has to fetch something before it can render. Nothing in this app does — every
 * route is bundled eagerly and none has a loader — so `Navigating` draws
 * nothing at all in practice. It is here because the alternative is that the
 * first route to grow a loader discovers this file does not exist, and because
 * a slow machine can spend long enough rendering a heavy screen for the
 * transition to be visible.
 *
 * **Nothing here appears immediately**, which is the rule that keeps it from
 * making things worse. See `useSlow`.
 *
 * ## The second threshold (#135)
 *
 * The mark waiting is the right answer for the two hundred milliseconds a
 * session check usually takes. It is not the right answer for the times it
 * takes two seconds: a logo pulsing in an empty window for that long is the
 * same picture as a page that has hung, and there is nothing to read while it
 * does.
 *
 * So the first thing anybody sees of this product is a boot screen now — the
 * mark and the wordmark, a line saying what is happening, a bar sweeping along
 * the bottom edge — and once the wait has gone on long enough to read
 * something, a tip about GOODBUILD arrives at the foot of it and is replaced
 * every few seconds until the wait ends. Games have filled loading screens this
 * way for thirty years, and the reason is the one that applies here: the wait
 * is not shorter, but it stops being dead.
 *
 * That is two thresholds rather than one, and they answer different questions.
 * `SETTLE_MS` asks *is this wait worth mentioning at all*. `TIP_MS` asks *is
 * this wait long enough that a sentence can be read before it disappears* —
 * which is a much longer time, and getting it wrong in the generous direction
 * costs nothing while getting it wrong in the other direction flashes a
 * paragraph at somebody.
 */

/**
 * How long a wait has to last before it is worth saying anything about.
 *
 * Under this, a loader is a flash: it appears and is replaced inside the time
 * it takes to read that something appeared, which reads as a flicker rather
 * than as feedback. Over it, silence reads as nothing happening.
 *
 * 140ms is inside the window where a person still attributes the result to
 * their own click, so a navigation that lands under it feels instant and gets
 * no loader at all — which is the right answer for a warm session check and for
 * every navigation in this app as it stands.
 */
const SETTLE_MS = 140;

/**
 * How long a wait has to last before a tip is worth putting in front of somebody.
 *
 * An order of magnitude past `SETTLE_MS`, and deliberately so. A tip is a
 * sentence to be read, and a sentence that appears and is gone before it has
 * been read is worse than no sentence — it registers as something having
 * flickered, which is the exact failure `SETTLE_MS` exists to prevent, at a
 * size somebody actually notices.
 *
 * 900ms is past the point where a wait has stopped feeling like part of the
 * click that caused it and started feeling like waiting. Under it the boot
 * screen is the mark and the wordmark on clean ground, which is the whole of
 * what a fast session check ever draws.
 *
 * The cost of being wrong here is asymmetric, which is why the number is
 * generous rather than tuned. Too late means somebody waited a second longer
 * before getting something to read. Too early means a paragraph flashed at
 * them. Only one of those is worth avoiding.
 */
const TIP_MS = 900;

/**
 * How long each tip stays before the next one.
 *
 * Long enough to read two sentences without hurrying, short enough that a slow
 * boot is not one sentence for its whole length. Six seconds is roughly twice
 * how long the longest tip in the pool takes to read, and the pool is ten deep
 * per surface — so a wait would have to last a full minute before anybody saw
 * the same tip twice.
 */
const TIP_ROTATE_MS = 6_000;

/**
 * Whether something has been waiting long enough to be worth drawing.
 *
 * False while `active` is false, false for the first `SETTLE_MS` of it being
 * true, and true after that until it goes false again.
 *
 * The timer is cleared when `active` goes false, so a wait that resolves inside
 * the window never schedules a render at all — this is not a delayed loader
 * that then hides itself, it is a loader that was never shown.
 */
export function useSlow(active: boolean, delay: number = SETTLE_MS): boolean {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (!active) {
      setSlow(false);
      return;
    }

    const timer = setTimeout(() => setSlow(true), delay);
    return () => clearTimeout(timer);
  }, [active, delay]);

  return slow;
}

/**
 * Which tip is showing, and the clock that moves it on.
 *
 * `active` is the `TIP_MS` gate rather than "is anything waiting": the interval
 * is not started until the wait has proved itself long, so a fast session check
 * schedules nothing at all and the hook costs a `useState` and a no-op effect.
 *
 * **The starting tip is random, and that is the point of it being random.** A
 * fixed start means the first tip in the pool is the only one most people ever
 * see, because most waits end during the first six seconds — so nine of the ten
 * would be written for nobody. Picked once in the state initialiser rather than
 * on every render, so the tip does not change when something else re-renders
 * the screen.
 *
 * Counting up and taking a remainder rather than wrapping the index, so the
 * order is stable and a rotation cannot land on the tip it just left.
 *
 * Returns `undefined` while it is not active, which `BootScreen` reads as *do
 * not draw the footer at all* — the same value it gets during a short wait,
 * rather than an empty string that would draw an empty band.
 */
export function useRotatingTip(tips: readonly string[], active: boolean): string | undefined {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * Math.max(1, tips.length)));

  useEffect(() => {
    if (!active || tips.length < 2) return;

    const timer = setInterval(() => setIndex((current) => current + 1), TIP_ROTATE_MS);
    return () => clearInterval(timer);
  }, [active, tips.length]);

  if (!active || tips.length === 0) return undefined;
  return tips[index % tips.length];
}

/**
 * The whole window, waiting.
 *
 * For the states where there is no page yet — the session check, and the frame
 * between deciding to send somebody elsewhere and the browser going. Renders
 * nothing for the first `SETTLE_MS`, so a session that comes back quickly is
 * still the silent, instant load it was before.
 *
 * **What it renders after that is the boot screen** (#135) rather than the bare
 * mark `PageLoader` draws. The two are the same thing at different lengths: at
 * `SETTLE_MS` it is the mark and the wordmark on the arriving surface's ground,
 * and at `TIP_MS` a tip and a rule arrive at the foot of it. Nothing about the
 * middle of the screen moves when that happens — see `.foot` in
 * `boot.module.css`, which is pinned for exactly this reason.
 *
 * `PageLoader` is still what a *navigation* draws, over the page rather than
 * instead of it. A boot screen belongs to a cold start; putting one over a page
 * somebody is already reading would be a bigger interruption than the wait it
 * is describing. See `Navigating`.
 *
 * `space` is passed rather than read, because this renders outside `Page` and
 * there is no `[data-gb-space]` above it to inherit from. `router.tsx` knows
 * the answer: it is already computing it for the favicons. It picks the tips
 * as well as the colours — a studio waiting on its portal gets tips about
 * builds and payouts rather than about the refund button.
 */
export function Waiting({
  label,
  space,
  /**
   * Skip the settle window.
   *
   * For a wait that is *known* to be long: a redirect has already committed to
   * a full page load, so there is nothing to be gained by holding the mark back
   * and a blank frame to be lost by it.
   *
   * **It does not skip `TIP_MS`.** Committed-to-be-long is not the same fact as
   * long-enough-to-read-a-sentence — a redirect on a fast connection is over in
   * a few hundred milliseconds — and the rule that a tip only appears when
   * there is time to read it has no exceptions worth making.
   */
  now = false,
}: {
  label?: string;
  space: SiteId;
  now?: boolean;
}): React.ReactElement {
  const slow = useSlow(!now);
  const readable = useSlow(true, TIP_MS);
  const tip = useRotatingTip(tipsFor(space), readable);

  useSoundOnMount('identSounded', now || slow);

  if (!now && !slow) return <></>;
  return <BootScreen space={space} {...(label ? { label } : {})} {...(tip ? { tip } : {})} />;
}

/**
 * A navigation that is taking long enough to say so.
 *
 * Over the page rather than instead of it: the screen being left is still
 * correct until the next one is ready, and blanking it to show a loader would
 * be the thing this file exists to stop.
 *
 * `status` rather than `isLoading`, which is the same fact for a router with no
 * pending-component timings configured, but `status` is the one the type
 * documents as the navigation's own state.
 *
 * **This draws nothing in this app today**, and that is the honest description
 * rather than a defect: every route is imported eagerly in `router.tsx` and
 * none declares a loader, so a navigation resolves within a frame and never
 * reaches `SETTLE_MS`. It is wired up so that the first route that does have
 * something to fetch is covered by it rather than blanking, and so that a slow
 * machine rendering one of the heavier screens has an answer.
 */
export function Navigating({ space }: { space: SiteId }): React.ReactElement {
  const pending = useRouterState({ select: (state) => state.status === 'pending' });
  const slow = useSlow(pending);

  if (!slow) return <></>;
  return <PageLoader space={space} over label="Loading the page" />;
}
